import { Document } from "../../document";
import { EntityAnnotation } from "../../annotation/interfaces/annotation";
import { User } from "../../user";
import { Status } from "../../common/status/status";
import { Dayjs } from "dayjs";
import { KeyByAny } from "./overview";
import { HoldReason } from "../../documentTypes/interfaces";

export interface InputType {
  id: number;
  name: string;
}

export interface Input {
  id: string;
  displayName: string;
  legacyAnnotator: boolean;
  attribute: KeyByAny;
}

export interface EmailReply {
  replyText: string;
}

export interface Sender {
  address: string;
  name: string;
}

export interface Email {
  id: number;
  sender: Sender;
  emailReply?: EmailReply;
}

export interface LogWebhookRequest {
  id: number;
  responseBody: Generic;
  responseStatusCode: number;
  createdDate: Date;
}

export interface ItemBody {
  contentType: string;
  content: string;
}

// Deliberately `any`: this models an arbitrary JSON bag (document set
// `metadata`, webhook `responseBody`) whose shape is defined per-customer by
// backend configuration and is not knowable here. `unknown` would force a cast
// at every one of the read sites that index into it.
export interface Generic {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

export interface DocumentSetColumns {
  id: string;
  input: Input;
  name: string;
  modifiedDate: Dayjs;
  createdDate: Dayjs;
  approvedDate: Dayjs;
  documents?: Array<Document>;
  assignee?: User;
  approvedBy?: User;
  annotatedBy?: User;
  status: Status;
  email?: Email;
  metadata?: Generic;
  searchable: string;
  logWebhookRequests: Array<LogWebhookRequest>;
  isDuplicate?: boolean;
  duplicateOfId?: string;
  duplicateOfName?: string;
  annotationVersion?: number;
  annotationVersionRevisions?: { [key: string]: number };
  editingAfterExport?: boolean;
  holdReason?: HoldReason;
}

export type DocumentSetData = DocumentSetColumns;

/**
 * The entity annotations of a document set as they were at a given annotation version.
 * Version 0 is the state at export. Categorization annotations are not versioned — the
 * detail page keeps showing the current ones next to a snapshot.
 */
export interface AnnotationSnapshot {
  version: number;
  revision: number;
  documents: Array<{
    documentId: string;
    entityAnnotations: Array<EntityAnnotation>;
  }>;
}

export type CounterState = {
  overview: number;
  error: number;
  remove: number;
};

export enum DocumentSetFetchingErrorType {
  INVALID_QUERY = "invalidQuery",
  OTHER_ERROR = "otherError",
}

export interface AdjacentDocumentSetIds {
  previousDocumentSetId: string;
  nextDocumentSetId: string;
}

export interface ColumnProperties {
  width: number;
  hasCellPadding: boolean;
}

export type ColumnConfig = {
  [key: string]: ColumnProperties;
};

export interface BulkActionValidationFlags {
  hasExportedDocs: boolean;
  hasOnlyAssignedToCurrentUser: boolean;
  hasNoRemoveStatus: boolean;
  hasNoProcessingStatus: boolean;
  hasOnlyAllowedStatuses: boolean;
  canReExport: boolean;
  canReApprove: boolean;
  canReprocess: boolean;
  hasAssignedDocs: boolean;
}

export interface BulkActionConfig {
  action: string;
  enabled: boolean;
  tooltip: string;
}
